import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, Input, input, OnInit, output, signal } from '@angular/core';
import { ControlValueAccessor, NgControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { asyncScheduler, Subject } from 'rxjs';
import { finalize, first, throttleTime } from 'rxjs/operators';

import { ClickActionEvent, FileUtils, StringStringMap } from '@bn/primitives';
import { FormatBytePipe } from '@bn/shared/core/pipes';

import { ButtonComponent } from '../button/button.component';
import { FormcontrolErrorsComponent } from '../standalone/formcontrol-errors/formcontrol-errors.component';

import { FilePreviewPage } from './components/file-preview/file-preview.page';
import { ExtendedFile } from './models';

@Component({
  selector: 'bn-web-ui-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
  imports: [ButtonComponent, FaIconComponent, ReactiveFormsModule, NgClass, FormcontrolErrorsComponent, FormatBytePipe, TooltipModule],
})
export class UploadFileComponent implements ControlValueAccessor, OnInit {
  ngControl = inject(NgControl, { self: true });

  readonly #dialog = inject(BsModalService);
  readonly #fb = inject(NonNullableFormBuilder);
  readonly #formatByte = inject(FormatBytePipe);
  isTouched = false;
  isFocused = false;
  isDraggedOver = false;
  isRequired!: boolean;
  files: ExtendedFile[] = [];
  customError = '';

  readonly allowedFileTypesLabel = input<string | undefined>('');
  /** Locks the drop zone, the browse input and the remove button. */
  readonly disabled = input<boolean>(false);
  readonly browseLinkText = input<string | undefined>('');
  readonly buttonText = input<string | undefined>('Drag & Drop to Upload');
  readonly existingFiles = input<ExistingUploadedFile[] | undefined>([]);
  readonly isLoading = input<boolean>(false);
  readonly label = input<string | undefined>('');
  readonly showAllowedFileTypesLabel = input<boolean | undefined>(true);
  readonly title = input<string | undefined>('');

  @Input()
  public itemStyle?: 'narrow-y';

  @Input()
  public isMultiple?: boolean = true; // undefined disables this attribute

  @Input()
  public error = false;

  @Input()
  public success = false;

  @Input()
  public allowedFileTypes?: string[];

  @Input()
  public minFileSize?: number;

  @Input()
  public maxFileSize?: number;

  @Input()
  public showUploadedFiles = true;

  @Input()
  public allowMultipleFiles = true;

  readonly action = output<ClickActionEvent>();

  /** Set by `setDisabledState` when the bound control is disabled. */
  readonly #controlDisabled = signal(false);

  /** Either source locks the component; the template and every handler read this one. */
  readonly isDisabled = computed(() => this.disabled() || this.#controlDisabled());

  dragOverEvent: Subject<Event> = new Subject<Event>();
  dragLeaveEvent: Subject<Event> = new Subject<Event>();

  form = this.#fb.group({
    fileInput: this.#fb.control<FileList | File[] | undefined>(undefined),
  });

  private onChange: (value: FileList | File[]) => void = () => ({});
  private onTouched: () => void = () => ({});

  constructor() {
    const ngControl = this.ngControl;

    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    // The native input carries its own `disabled`, so the picker cannot be opened through the
    // label either — the CSS class alone only stops the mouse.
    effect(() => {
      const control = this.form.get('fileInput');

      if (this.isDisabled()) control?.disable({ emitEvent: false });
      else control?.enable({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.isRequired = this.ngControl.control?.hasValidator(Validators.required) || false;
  }

  onClick(): void {
    if (this.isDisabled()) return;

    this.form.get('fileInput')?.setValue(undefined);
  }

  onRemoveFile(event: MouseEvent | Event, fileId: number): void {
    event.preventDefault();
    if (this.isDisabled()) return;
    // remove file from list
    this.files.splice(fileId, 1);
    // revalidate on changed file array
    this.#handleUploadFiles([]);
  }

  onRemoveExistingFile(itemId: string): void {
    if (this.isDisabled()) return;

    // this.#dialog
    //   .show(ConfirmationDialogComponent, {
    //     class: 'modal-dialog-centered modal-sm',
    //     initialState: {
    //       data: {
    //         confirmButtonName: 'Remove thumbnail',
    //         description: 'Removing the default live stream thumbnail will revert to the system generated thumbnail.',
    //         contextType: 'warning',
    //         icon: 'square-exclamation',
    //         event: {
    //           action: 'confirm',
    //         },
    //         title: 'Remove default live stream thumbnail?',
    //         textCenter: true,
    //       },
    //     },
    //   })
    //   .content?.getOnAction$()
    //   .pipe(
    //     first(), // one time
    //     finalize(() => this.#dialog.hide()), // always
    //     tap(({ action }) => {
    //       if (action === 'confirm') {
    //         this.action.emit({
    //           action: 'delete',
    //           itemId,
    //         });
    //       }
    //     }),
    //   )
    //   .subscribe();

    this.action.emit({
      action: 'delete',
      itemId,
    });
  }

  onDragOver(event: Event): void {
    event.preventDefault();
    if (this.isDisabled()) return;

    if (this.dragOverEvent.observers.length === 0 && !this.isDraggedOver) {
      this.dragOverEvent.pipe(throttleTime(1000, asyncScheduler, { leading: true, trailing: true })).subscribe(() => {
        this.isDraggedOver = true;
      });
    }
    this.dragOverEvent.next(event);
  }

  onDragLeave(event: Event): void {
    event.preventDefault();

    if (this.dragLeaveEvent.observers.length === 0 && !this.isDraggedOver) {
      this.dragLeaveEvent.pipe(throttleTime(1000, asyncScheduler, { leading: true, trailing: true })).subscribe(() => {
        this.isDraggedOver = false;
      });
    }
    this.dragLeaveEvent.next(event);
  }

  writeValue(value: File[]): void {
    this.files = (value || [])
      .filter((file) => !!this.allowedFileTypes?.length || this.allowedFileTypes?.includes(FileUtils.getFileExtension(file.name).toLowerCase()))
      .map((file) => ({
        file,
        icon: FileUtils.getIconForFilename(file.name),
      }));
    this.form.controls.fileInput.setValue(value);
  }

  registerOnChange(fn: (value: FileList | File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.#controlDisabled.set(isDisabled);
  }

  onTouch(): void {
    this.isFocused = false;

    if (this.isTouched) {
      return;
    }

    this.isTouched = true;

    this.onTouched();
  }

  onUpload(event: Event): void {
    if (this.isDisabled()) return;

    const files = Array.from((event.target as HTMLInputElement).files ?? []);
    this.#handleUploadFiles(files);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.isDisabled()) return;

    const files = Array.from(event.dataTransfer?.files ?? []);
    this.#handleUploadFiles(files);
  }

  onShowPreview(url: string): void {
    // const width = 1280;
    // const height = 720;
    // const left = (screen.width - width) / 2;
    // const top = (screen.height - height) / 2;
    // const windowFeatures = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes`;
    //
    // window.open(url, '_blank', windowFeatures);
    this.#dialog
      .show(FilePreviewPage, {
        id: 'upload-preview-stream',
        class: 'modal-dialog-centered modal-lg',
        initialState: {
          data: {
            item: url,
            extra: {
              media: 'image',
            },
          },
        },
      })
      .content?.getOnAction$()
      .pipe(
        first(), // one time
        finalize(() => this.#dialog.hide()), // always
      )
      .subscribe();
  }

  #handleUploadFiles(newFiles: File[]): void {
    newFiles
      .filter((file) => !this.allowedFileTypes?.length || this.allowedFileTypes?.includes(FileUtils.getFileExtension(file.name).toLowerCase()))
      .forEach((file) => {
        const extendedFile = {
          file,
          icon: FileUtils.getIconForFilename(file.name),
        };
        // override existing file if only one file is allowed
        if (!this.allowMultipleFiles) {
          this.files[0] = extendedFile;
        }

        // override existing files with same name
        const existingIndex = this.files.findIndex(({ file: meta }) => meta.name === file.name);

        if (existingIndex === -1) this.files.push(extendedFile);
        else this.files[existingIndex] = extendedFile;
      });

    const passed = this.#filesPassedValidation();
    if (passed || !this.files.length) this.onChange(this.#getBasicFiles());
  }

  #filesPassedValidation(): boolean {
    let allFilesMatch = this.files.length; // Presume all files are OK
    let fileErrors: StringStringMap = {};

    this.files.forEach(({ file: meta }) => {
      const fileExtension = FileUtils.getFileExtension(meta.name).toLowerCase();

      switch (true) {
        case this.allowedFileTypes?.length && !this.allowedFileTypes.includes(fileExtension):
          allFilesMatch -= 1;
          fileErrors = {
            ...fileErrors,
            [meta.name]: `Doesn't meet file type <strong>${this.allowedFileTypes.toString()}</strong>`,
          };
          break;
        case this.minFileSize !== undefined && meta.size < this.minFileSize:
          allFilesMatch -= 1;
          fileErrors = {
            ...fileErrors,
            [meta.name]: `Doesn't meet min file size of <strong>${this.#formatByte.transform(this.minFileSize).formatted}</strong>`,
          };
          break;
        case this.maxFileSize !== undefined && meta.size > this.maxFileSize:
          allFilesMatch -= 1;
          fileErrors = {
            ...fileErrors,
            [meta.name]: `Doesn't meet max file size of <strong>${this.#formatByte.transform(this.maxFileSize).formatted}</strong>`,
          };
          break;
        default:
          break;
      }
    });

    // Has errored?
    this.error = allFilesMatch !== this.files.length;
    // Push error to user
    if (this.error) {
      this.form.controls['fileInput'].setErrors({ uploadFileError: true });
      this.customError = Object.entries(fileErrors)
        .map(([name, error]) => `${name}: ${error}`)
        .join('\n');
    } else {
      this.form.controls['fileInput'].setErrors(null);
      this.customError = '';
    }

    return !this.error;
  }

  #getBasicFiles(): File[] {
    return this.files.map(({ file }) => file);
  }
}

export type ExistingUploadedFile = {
  id: string;
  title: string;
  size?: number;
  url: string;
  icon?: string;
};
